import { z } from 'zod'

/**
 * Offset/page-based pagination — matches the shape the mobile app's feed
 * pipeline already uses (apps/app/services/feed/feed-service.interface.ts
 * `PaginatedResponse<T>` and its `PaginationHandler`). One contract for the
 * whole monorepo: backend serves it, mobile consumes via PaginationHandler,
 * dashboard consumes via `useInfiniteQuery` with `getNextPageParam` reading
 * `pagination.page + 1` while `page < totalPages`.
 *
 * Tradeoff vs. cursor pagination: offset is fine for "jump to page 17" UX
 * but mildly unstable under concurrent writes (an insert can shift items
 * onto the next page → a dupe; a delete can shift items earlier → a skip).
 * For dashboard business-events / business-listings the writer IS the
 * viewer, so concurrent-write races are negligible. Keeping the shape
 * uniform across the app + dashboard is the higher-value invariant.
 */

export const PAGINATION_MAX_PAGE_SIZE = 100
// Hard offset ceiling: crawlers walking public pagination to page ~750 drove
// OFFSET-18k PostGIS scans that timed out the whole database (2026-07-15).
// No human paginates this deep; reject instead of scanning.
export const PAGINATION_MAX_PAGE = 200

/**
 * Query parameters a paginated GET endpoint accepts.
 *
 *   page     — 1-indexed; defaults to 1 because "page 1" is the obvious
 *              first call and nothing about the client UI decides it.
 *   pageSize — REQUIRED. The server intentionally doesn't pick this for
 *              you. Each consumer's UI knows what fits — the dashboard's
 *              tables, the mobile feed cards, the friends list — and a
 *              hidden server default just hides that decision from the
 *              call site. Clients must declare it; the server caps at
 *              {@link PAGINATION_MAX_PAGE_SIZE}.
 *
 * `z.coerce.number()` so query-string values ("1", "20") parse correctly
 * while the type-side stays `number`.
 */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(PAGINATION_MAX_PAGE).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGINATION_MAX_PAGE_SIZE),
})
export type PaginationQuery = z.infer<typeof paginationQuerySchema>

/**
 * The pagination envelope returned alongside `data`. Field names + meaning
 * match `apps/app/services/feed/feed-service.interface.ts` so the mobile
 * PaginationHandler reads server responses without translation.
 *
 *   page         — current page number (1-indexed)
 *   pageSize     — items requested per page (≤ {@link PAGINATION_MAX_PAGE_SIZE})
 *   total        — total items the underlying query matches
 *   totalPages   — Math.ceil(total / pageSize)
 *
 * `hasMore` is intentionally NOT a field — consumers derive it via
 * `page < totalPages` (which is exactly what the app's PaginationHandler does
 * at apps/app/utils/data/PaginationHandler.ts:142).
 */
export const paginationInfoSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
})
export type PaginationInfo = z.infer<typeof paginationInfoSchema>

/**
 * Wraps any item schema with the standard pagination envelope. Use this on
 * every new paginated response schema so the shape stays uniform:
 *
 *   export const eventListResponseSchema = paginatedResponseSchema(eventDtoSchema)
 *
 * Returns a fresh z.object each call (zod schemas are immutable, so this is
 * safe to invoke at module top-level).
 */
export function paginatedResponseSchema<TItem extends z.ZodType>(item: TItem) {
  return z.object({
    data: z.array(item),
    pagination: paginationInfoSchema,
  })
}

/**
 * `PaginatedResponse<EventDto>` for code that needs the static type without
 * pulling in the runtime schema. Field-for-field identical to the app's
 * `PaginatedResponse<T>` interface so it can eventually replace that local
 * definition (`apps/app/services/feed/feed-service.interface.ts:8`) without
 * a behavior change — just an import swap.
 */
export interface PaginatedResponse<TItem> {
  data: TItem[]
  pagination: PaginationInfo
}

export const WINDOW_BEFORE_HINT = 100
export const WINDOW_AFTER_HINT = 300
export const WINDOW_SIZE = WINDOW_BEFORE_HINT + WINDOW_AFTER_HINT

export const windowedQuerySchema = z.object({
  startIndex: z.coerce.number().int().min(0).default(0),
  pageSize: z.coerce.number().int().min(1).max(PAGINATION_MAX_PAGE_SIZE),
})
export type WindowedQuery = z.infer<typeof windowedQuerySchema>

/**
 * Windowed list envelope. `nextStartIndex` is `null` when no more items
 * exist past this slice; clients derive `hasMore` from it.
 */
export const windowedInfoSchema = z.object({
  startIndex: z.number().int().min(0),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  nextStartIndex: z.number().int().min(0).nullable(),
})
export type WindowedInfo = z.infer<typeof windowedInfoSchema>

export function windowedResponseSchema<TItem extends z.ZodType>(item: TItem) {
  return z.object({
    data: z.array(item),
    window: windowedInfoSchema,
  })
}

export interface WindowedResponse<TItem> {
  data: TItem[]
  window: WindowedInfo
}
